/*
 * Copyright 2026, Airspan
 * All Rights Reserved.
 */

import React, { useLayoutEffect, useRef } from "react";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import { Check, X, Pencil } from "lucide-react";

interface EditableTextCellProps {
  value: string;
  displayValue?: string;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onEditStart: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
}

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  displayValue,
  isEditing,
  editValue,
  onEditValueChange,
  onEditStart,
  onCancel,
  onSave,
  isSaving = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectionRef = useRef<{ start: number | null; end: number | null }>({ start: null, end: null });

  useLayoutEffect(() => {
    if (!isEditing || !inputRef.current) {
      return;
    }
    inputRef.current.focus();
    const { start, end } = selectionRef.current;
    if (start != null && end != null) {
      inputRef.current.setSelectionRange(start, end);
    }
  }, [isEditing, editValue]);

  const stopGridKeyHandling = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (typeof event.nativeEvent.stopImmediatePropagation === "function") {
      event.nativeEvent.stopImmediatePropagation();
    }
  };

  const handleSelect = () => {
    if (!inputRef.current) {
      return;
    }
    selectionRef.current = {
      start: inputRef.current.selectionStart,
      end: inputRef.current.selectionEnd,
    };
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 w-full">
        <Input
          ref={inputRef}
          value={editValue}
          autoFocus
          onChange={(event) => {
            event.stopPropagation();
            onEditValueChange(event.target.value);
            handleSelect();
          }}
          onBlur={() => onSave()}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={stopGridKeyHandling}
          onKeyDownCapture={stopGridKeyHandling}
          onKeyUpCapture={stopGridKeyHandling}
          onSelect={handleSelect}
          disabled={isSaving}
          
          className="flex-1 h-8"
        />
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-1 w-full">
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
        {displayValue ?? value}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="inline-flex items-center justify-center rounded-md text-sm h-8 w-8 bg-transparent transition-colors duration-150 hover:bg-accent invisible group-hover:visible"
        onClick={(event) => {
          event.stopPropagation();
          onEditStart();
        }}
      >
        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
      </Button>
    </div>
  );
};

export default EditableTextCell;
